import _, { round } from 'lodash';
import { callApi } from 'src/core/callApi';
import { User } from 'src/interfaces/iuser';
import { convertCurrency, getLicenseStatus } from 'src/utils/functions';

const calculateLtmAverage = (apiResponse, kpiField, offsetMonths = 12) => {
  const monthlyEntries = Object.entries(apiResponse.historical_monthly_performance);

  monthlyEntries.sort((a, b) => {
    const dateA = new Date(a[0]).getTime();
    const dateB = new Date(b[0]).getTime();
    return dateA - dateB;
  });

  const totalMonths = monthlyEntries.length;
  let endIndex = totalMonths - 1 - offsetMonths;
  let startIndex = Math.max(0, endIndex - 11);

  let sum = 0;
  let count = 0;
  for (let i = startIndex; i <= endIndex; i++) {
    const [, data] = monthlyEntries[i] as [string, Record<string, number>];
    if (data[kpiField] !== null && data[kpiField] !== undefined) {
      sum += data[kpiField];
      count++;
    }
  }
  return count > 0 ? sum / count : null;
};

export const KPI_CONFIG = {
  ANY_BEDROOM: {
    occupancy_rate: {
      path: 'ltm_performance.occupancy_rate',
      needsCurrencyConversion: false,
      symbol: '%',
    },
    adr: {
      path: 'ltm_performance.adr',
      needsCurrencyConversion: true,
      symbol: 'currency',
    },
    revenue: {
      path: 'ltm_performance.revenue',
      needsCurrencyConversion: true,
      symbol: 'currency',
    },
    listings: {
      path: 'ltm_performance.supply',
      needsCurrencyConversion: false,
      symbol: '',
    },
    adr_25th: {
      path: 'ltm_performance.adr_25',
      needsCurrencyConversion: true,
      symbol: 'currency',
    },
    adr_75th: {
      path: 'ltm_performance.adr_75',
      needsCurrencyConversion: true,
      symbol: 'currency',
    },
    adr_90th: {
      path: 'ltm_performance.adr_90',
      needsCurrencyConversion: true,
      symbol: 'currency',
    },
    occupancy_rate_25th: {
      path: 'ltm_performance.occupancy_rate_25',
      needsCurrencyConversion: false,
      symbol: '%',
    },
    occupancy_rate_75th: {
      path: 'ltm_performance.occupancy_rate_75',
      needsCurrencyConversion: false,
      symbol: '%',
    },
    occupancy_rate_90th: {
      path: 'ltm_performance.occupancy_rate_90',
      needsCurrencyConversion: false,
      symbol: '%',
    },
    revenue_25th: {
      path: 'ltm_performance.revenue_25',
      needsCurrencyConversion: true,
      symbol: 'currency',
    },
    revenue_75th: {
      path: 'ltm_performance.revenue_75',
      needsCurrencyConversion: true,
      symbol: 'currency',
    },
    revenue_90th: {
      path: 'ltm_performance.revenue_90',
      needsCurrencyConversion: true,
      symbol: 'currency',
    },
    occupancy_rate_yoy: {
      path: 'yearly_performance_changes.one_year_changes.occupancy_changes',
      needsCurrencyConversion: false,
      symbol: '%',
    },
    adr_yoy: {
      path: 'yearly_performance_changes.one_year_changes.adr_changes',
      needsCurrencyConversion: false,
      symbol: '%',
    },
    revenue_yoy: {
      path: 'yearly_performance_changes.one_year_changes.revenue_changes',
      needsCurrencyConversion: false,
      symbol: '%',
    },
    supply_yoy: {
      path: 'yearly_performance_changes.one_year_changes.supply_changes',
      needsCurrencyConversion: false,
      symbol: '',
    },
    occupancy_rate_2yr: {
      path: 'yearly_performance_changes.two_year_changes.occupancy_changes',
      needsCurrencyConversion: false,
      symbol: '%',
    },
    adr_2yr: {
      path: 'yearly_performance_changes.two_year_changes.adr_changes',
      needsCurrencyConversion: true,
      symbol: '%',
    },
    revenue_2yr: {
      path: 'yearly_performance_changes.two_year_changes.revenue_changes',
      needsCurrencyConversion: true,
      symbol: '%',
    },
    supply_2yr: {
      path: 'yearly_performance_changes.two_year_changes.supply_changes',
      needsCurrencyConversion: false,
      symbol: '',
    },
    cleaning_fee: {
      path: 'extra_pricing.cleaning_fee',
      needsCurrencyConversion: true,
      symbol: 'currency',
    },
    hosts_1_5: {
      path: 'professional_host_distribution.1-5_host',
      needsCurrencyConversion: false,
      symbol: '',
    },
    hosts_6_20: {
      path: 'professional_host_distribution.6-20_host',
      needsCurrencyConversion: false,
      symbol: '',
    },
    hosts_20plus: {
      path: 'professional_host_distribution.20+_host',
      needsCurrencyConversion: false,
      symbol: '',
    },
    listings_pool: {
      path: 'supply_breakdown.amenity.pool',
      needsCurrencyConversion: false,
      symbol: '%',
    },
    listings_hot_tub: {
      path: 'supply_breakdown.amenity.hot_tub',
      needsCurrencyConversion: false,
      symbol: '%',
    },
    common_supply_share: {
      path: 'yearly_performance_changes.common_supply_share',
      needsCurrencyConversion: false,
      symbol: '%',
    },
    license_percentage: {
      path: 'supply_breakdown.license_percentage',
      needsCurrencyConversion: false,
      symbol: '%',
      functionName: getLicenseStatus,
    },
  },
  BEDROOM_SPECIFIC: {
    occupancy_rate: {
      path: 'occupancy_breakdown.bedroom',
      needsCurrencyConversion: false,
    },
    adr: {
      path: 'adr_breakdown.bedroom',
      needsCurrencyConversion: true,
    },
    revenue: {
      path: 'revenue_breakdown.bedroom',
      needsCurrencyConversion: true,
    },
    listings: {
      path: 'supply_breakdown.bedroom',
      needsCurrencyConversion: false,
    },
  },
};

export const getNestedValue = (obj: any, path: string, bedroomKey?: string): any => {
  const value = path.split('.').reduce((acc, key) => acc?.[key], obj);
  return bedroomKey ? value?.[bedroomKey] : value;
};

export const triggerMarketCache = (targetMarketName, comparisons) => {
  try {
    const targetMarketInfo = comparisons.filter(market => market.label === targetMarketName)[0]
      .details;
    const url = 'https://68gjq7no20.execute-api.us-east-2.amazonaws.com/default/getDistrictInfo';

    const config = {
      district: targetMarketInfo['name'],
      city: targetMarketInfo['city'],
      country: targetMarketInfo['country'],
    };
    callApi(url, { queryParams: config }, true);
  } catch (err) {
    console.error('Error while triggering market cache: ', err);
  }
};

export const processMarketData = (
  data,
  selectedBedroom,
  currency,
  comparisons,
  propertyPriceMarketWise = {},
  setComparison: any = null
) => {
  const marketDataRow: any[] = [];
  const anyBedroomFilterSelected = selectedBedroom === 'any';
  const kpiConfig = anyBedroomFilterSelected ? KPI_CONFIG.ANY_BEDROOM : KPI_CONFIG.BEDROOM_SPECIFIC;

  let index: number = 0;
  for (const market in data) {
    const marketData: any = data[market]['message'];

    if (!marketData) {
      if (setComparison) {
        setComparison(prev => prev.filter(comp => comp.label !== market));
      }
      continue;
    }

    const isCacheOutdated = marketData['is_cache_outdated'];

    isCacheOutdated && triggerMarketCache(market, comparisons);

    if (isCacheOutdated) {
      marketDataRow.push({
        id: index++,
        name: market,
        occupancy_rate: '',
        adr: '',
        revenue: '',
        listings: '',
        isCacheOutdated: true,
      });
      continue;
    }

    const rowData: any = {
      id: index++,
      name: market,
      property_price: propertyPriceMarketWise[market] || '',
      yield: '-',
    };

    // Process each KPI based on configuration
    Object.entries(kpiConfig).forEach(([kpiKey, config]) => {
      let value = getNestedValue(
        marketData,
        config.path,
        anyBedroomFilterSelected ? undefined : selectedBedroom.toString()
      );

      // If there's a function specified in the config, apply it first
      if ((config as any).functionName) {
        const functionToApply = eval((config as any).functionName);
        if (typeof functionToApply === 'function') {
          const processedValue = functionToApply(value);
          value = processedValue;
        }
      }

      // Apply currency conversion if needed
      if (config.needsCurrencyConversion && value !== null && value !== undefined) {
        rowData[kpiKey] = round(convertCurrency(currency, value), 0);
      } else {
        rowData[kpiKey] = value;
      }

      if (rowData[kpiKey] === null || rowData[kpiKey] === undefined) {
        rowData[kpiKey] = '-';
      }
    });

    marketDataRow.push(rowData);
  }

  return marketDataRow;
};

export const processReportData = (data: any, currency: string) => {
  const marketDataRow: any[] = [];

  let index: number = 0;
  for (const reportId in data) {
    const report: any = data[reportId];
    if (!report) return marketDataRow;

    const reportKpi = report['kpi']['50'];
    const editedReportKpi = report['kpi']['edited'];

    const occupancyRate = editedReportKpi['ltm_occupancy_rate'] || reportKpi['ltm_occupancy_rate'];
    const revenue = editedReportKpi['ltm_revenue'] || reportKpi['ltm_revenue'];
    const adr = editedReportKpi['ltm_nightly_rate'] || reportKpi['ltm_nightly_rate'];

    const address = report['primary_input']['address_short'];
    const bedrooms = report['primary_input']['bedrooms'];
    const reportType = _.capitalize(report['primary_input']['report_type']);

    marketDataRow.push({
      id: index++,
      name: address,
      bedrooms: bedrooms,
      reportType: reportType,
      occupancy_rate: occupancyRate,
      adr: convertCurrency(currency, adr),
      revenue: convertCurrency(currency, revenue),
      currency: currency,
    });
  }

  return marketDataRow;
};

export const generateDownloadFilename = () => {
  const date = new Date();
  //  YYYYMMDD_HHMMSS
  const dateTime = date
    .toISOString()
    .replace(/[-:T]/g, '')
    .split('.')[0];
  // Generate a random 8 digit string
  const randomString = Math.random()
    .toString(36)
    .substring(2, 10)
    .toLowerCase();
  return `market_compare_${dateTime}_${randomString}.csv`;
};

export const processArrayToCsv = (data: { [key: string]: number | string }[], addHeader = true) => {
  if (!data.length) return '';

  const escapeCSVValue = (value: number | string) => {
    const str = String(value).replace(/"/g, '""');
    return str.includes(',') ? `"${str}"` : str;
  };

  let csv = data.map(row =>
    Object.values(row)
      .map(escapeCSVValue)
      .join(',')
  );

  if (addHeader) {
    const header = Object.keys(data[0]);
    csv.unshift(header.map(escapeCSVValue).join(','));
  }

  return csv.join('\n');
};

export const getSavedReports = async setData => {
  const url =
    'https://3iugob4wql.execute-api.us-east-2.amazonaws.com/prod/airbnbCalculatorReportCRUD';
  const data = await callApi(url, {}, true, false, '', 'GET', true);
  if (data && !data.error && data.reports) {
    setData(data.reports);
  }
};

export const metricKeyNameMap = {
  adr: `ADR (Median)`,
  adr_25th: `ADR (25th Percentile)`,
  adr_75th: `ADR (75th Percentile)`,
  adr_90th: `ADR (90th Percentile)`,
  occupancy_rate: 'Occupancy Rate (Median)',
  occupancy_rate_25th: 'Occupancy Rate (25th Percentile)',
  occupancy_rate_75th: 'Occupancy Rate (75th Percentile)',
  occupancy_rate_90th: 'Occupancy Rate (90th Percentile)',
  revenue: `Revenue (Median)`,
  revenue_25th: `Revenue (25th Percentile)`,
  revenue_75th: `Revenue (75th Percentile)`,
  revenue_90th: `Revenue (90th Percentile)`,
  listings: 'Listings',
  occupancy_rate_yoy: 'Occupancy Rate YoY Change',
  adr_yoy: 'ADR YoY Change',
  revenue_yoy: 'Revenue YoY Change',
  supply_yoy: 'Supply YoY Change',
  common_supply_share: 'supply still available from last year',
  occupancy_rate_2yr: 'Occupancy Rate 2-Year Change',
  adr_2yr: 'ADR 2-Year Change',
  revenue_2yr: 'Revenue 2-Year Change',
  supply_2yr: 'Supply 2-Year Change',
  cleaning_fee: `Cleaning Fees (Median)`,
  hosts_1_5: '% of Hosts with 1-5 Listings',
  hosts_6_20: '% of Hosts with 6-20 Listings',
  hosts_20plus: '% of Hosts with 20+ Listings',
  listings_pool: '% of Listings with Pool',
  listings_hot_tub: '% of Listings with Hot Tub',
};

export const getComparisionMaxMarketCountOnce = (user: User) => {
  const userSubscriptionPlan = user.subscriptionPlan;
  if (userSubscriptionPlan === 'scale') {
    return 20;
  } else {
    return 5;
  }
};
